import React from 'react'
import { Button, Dialog } from '@/components/ds'
import { Ic } from '@/components/icons'
import { QueryState } from '@/components/common'
import {
  ImportTemplateDialog,
  TemplateList,
  TemplateModeToggle,
  TemplatePreviewDialog,
  TemplateSplitEditor,
  TemplateViewer,
  createBlankTemplate,
  mergeFieldsFromLeads,
  previewLead,
  senderPreview,
  useRenderedTemplate,
  useTemplateDraft,
} from '@/components/template'
import {
  campaignsUsingTemplate,
  createTemplate,
  deleteTemplate,
  listCampaigns,
  listIdentities,
  listLeads,
  listTemplates,
} from '@/lib/db'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { htmlToText } from '@shared/render'
import type { Campaign, Template } from '@shared/types'
import type { ScreenProps } from '@/App'

/** One-shot: set before navigating here to open a fresh template editor. */
export const OPEN_NEW_TEMPLATE = 'lm-open-new-template'

/**
 * The template LIBRARY.
 *
 * Templates are standalone objects: you can write one before any campaign
 * exists, and the same template can back several campaigns (a campaign points
 * at one with campaigns.template_id, which is also what the sender resolves).
 * Editing here therefore changes every campaign using it — the delete confirm
 * says how many.
 *
 * Preview-first: selecting shows the shared viewer, "Edit template" opens the
 * shared split editor — the same two components the wizard's step 3 renders.
 */
export function TemplatesScreen({ narrow, setHeader, onNavigate }: ScreenProps) {
  const toast = useToast()
  const campaigns = useAsync(() => listCampaigns(), [])
  const identities = useAsync(() => listIdentities(), [])

  // THE LIBRARY: every template, newest first. No campaign filter, no gate.
  const templates = useAsync(() => listTemplates(), [])

  // Preview context. A template usually belongs to no campaign, so the sample
  // lead and the From/Reply-To come from a campaign that USES it when there is
  // one, and fall back to neutral defaults otherwise.
  const [activeId, setActiveId] = React.useState<string | null>(null)

  const usingCampaigns = (campaigns.data ?? []).filter((c) => c.template_id === activeId)
  const previewCampaign: Campaign | null = usingCampaigns[0] ?? null
  const campaignId = previewCampaign?.id ?? ''

  const leadPeek = useAsync(
    async () => {
      // With a campaign attached, page through ITS leads in the Leads-step
      // order. Without one (a template opened straight from this tab), fall
      // back to the account's first N so the preview still has real people to
      // page through instead of one synthetic sample.
      const { rows } = await listLeads(campaignId ? { campaignId, limit: 50 } : { limit: 25 })
      return rows
    },
    [campaignId],
  )

  const leadRows = leadPeek.data ?? []
  const sampleLead = previewLead(leadRows)
  const mergeFields = mergeFieldsFromLeads(leadRows)

  const [editing, setEditing] = React.useState(false)
  const [isNew, setIsNew] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState<Template | null>(null)
  const [showPreview, setShowPreview] = React.useState(false)
  const [importing, setImporting] = React.useState(false)

  // A template created here is authoritative until the list reload catches up,
  // so a fresh editor never snaps back to an older template mid-edit.
  const [pending, setPending] = React.useState<Template | null>(null)

  const rows = templates.data ?? []
  const stored =
    rows.find((t) => t.id === activeId) ?? (pending?.id === activeId ? pending : null)

  // Shared draft state — same dirty/save/mode-switch semantics as the wizard.
  const editor = useTemplateDraft(stored)
  const draft = editor.draft

  // Keep the selection pointed at a template that still exists.
  React.useEffect(() => {
    if (!rows.length) {
      setActiveId(null)
      setEditing(false)
      return
    }
    if (!rows.some((t) => t.id === activeId)) {
      setActiveId(rows[0].id)
      setEditing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.data])

  // Arriving via the Dashboard's "Create template" link: jump straight into a
  // fresh editor. Consuming the flag inside the condition keeps this idempotent
  // under StrictMode.
  React.useEffect(() => {
    if (!templates.loading && sessionStorage.getItem(OPEN_NEW_TEMPLATE)) {
      sessionStorage.removeItem(OPEN_NEW_TEMPLATE)
      void addTemplate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.loading])

  function select(t: Template) {
    setActiveId(t.id)
    editor.select(t)
    setEditing(false)
    setIsNew(false)
  }

  async function addTemplate() {
    setBusy(true)
    try {
      // Standalone: it joins the library and is bound to a campaign later,
      // from the wizard or the campaign's own Template field.
      const created = await createBlankTemplate(null)
      toast('success', 'Template created', 'Saved to your library')
      setPending(created)
      setActiveId(created.id)
      editor.select(created)
      setEditing(true)
      setIsNew(true)
      templates.reload()
    } catch (e) {
      toast('error', 'Create failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Creates an html-mode template from pasted markup, stored VERBATIM down the
   * same path the HTML editor uses, then opens it for editing.
   */
  async function importTemplate(input: { name: string; subject: string; html: string }) {
    setBusy(true)
    try {
      const created = await createTemplate({
        campaign_id: null,
        name: input.name.trim(),
        subject: input.subject.trim(),
        body_html: input.html,
        body_text: htmlToText(input.html),
        body_mode: 'html',
      })
      setImporting(false)
      toast('success', `${input.name.trim()} imported`, 'Stored as raw HTML in your library')
      setPending(created)
      setActiveId(created.id)
      editor.select(created)
      setEditing(true)
      setIsNew(false)
      templates.reload()
    } catch (e) {
      toast('error', 'Import failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    try {
      await editor.save()
      setEditing(false)
      setIsNew(false)
      toast('success', 'Template saved', 'Queued emails render with the new copy')
      templates.reload()
    } catch (e) {
      toast('error', 'Save failed', e instanceof Error ? e.message : String(e))
    }
  }

  function cancelEdit() {
    editor.revert()
    setEditing(false)
    setIsNew(false)
  }

  async function remove(t: Template) {
    setBusy(true)
    try {
      await deleteTemplate(t.id)
      toast('info', 'Template deleted', 'Removed from your library')
      setConfirmDelete(null)
      setActiveId(null)
      setEditing(false)
      templates.reload()
    } catch (e) {
      toast('error', 'Delete failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // --- topbar takeover while editing (title + Show preview / Save) ------------
  // Handlers go through refs so the header effect doesn't need to re-run on
  // every keystroke just to keep closures fresh.
  const saveRef = React.useRef(save)
  saveRef.current = save
  const cancelRef = React.useRef(cancelEdit)
  cancelRef.current = cancelEdit
  const modeRef = React.useRef(editor.switchMode)
  modeRef.current = editor.switchMode

  const working = busy || editor.saving

  React.useEffect(() => {
    if (!editing) {
      // Browse view also runs full-bleed so it can use its own tight padding.
      setHeader({ fullBleed: true })
      return
    }
    setHeader({
      title: isNew ? 'New template' : 'Edit template',
      fullBleed: true,
      actions: (
        <>
          <TemplateModeToggle mode={editor.mode} onSwitch={(m) => modeRef.current(m)} />
          <Button size="sm" variant="ghost" disabled={working} onClick={() => cancelRef.current()}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="secondary"
            style={{ borderRadius: 8, color: 'var(--accent-text)' }}
            onClick={() => setShowPreview(true)}
          >
            Show preview
          </Button>
          <Button
            size="sm"
            style={{ borderRadius: 8 }}
            disabled={working || !editor.dirty}
            onClick={() => saveRef.current()}
          >
            {editor.saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      ),
    })
    return () => setHeader(null)
  }, [editing, isNew, working, editor.saving, editor.dirty, editor.mode, setHeader])

  // The read viewer renders through the same hook the editor's live pane uses.
  const rendered = useRenderedTemplate(draft, sampleLead, 0)

  const canSendTest = (identities.data ?? []).some((i) => i.has_password)

  const sender = senderPreview(previewCampaign, identities.data ?? [])

  // Shared by both views so the preview modal / delete confirm survive either.
  const dialogs = (
    <>
      {importing && (
        <ImportTemplateDialog
          busy={busy}
          onClose={() => setImporting(false)}
          onImport={importTemplate}
        />
      )}
      {showPreview && draft && (
        <TemplatePreviewDialog
          draft={draft}
          sampleLead={sampleLead}
          canSend={canSendTest}
          campaignId={campaignId}
          onClose={() => setShowPreview(false)}
        />
      )}
      {confirmDelete && (
        <DeleteTemplateDialog
          template={confirmDelete}
          busy={busy}
          onClose={() => setConfirmDelete(null)}
          onDelete={() => remove(confirmDelete)}
        />
      )}
    </>
  )

  // ---- FULL-PAGE editor: the topbar carries title + Show preview/Save; the
  // campaign picker and template list are gone until Save/Cancel returns here.
  if (editing && draft) {
    return (
      <>
        <TemplateSplitEditor
          draft={draft}
          mergeFields={mergeFields}
          sampleLead={sampleLead}
          leads={leadRows}
          narrow={narrow}
          onSubject={editor.setSubject}
          onBody={editor.setBody}
        />
        {dialogs}
      </>
    )
  }

  // ---- browse view: campaign picker, template list, rendered preview --------
  return (
    // fullBleed gives this screen its own tight padding instead of the shell's
    // 18px, so the viewer sits close under the topbar.
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: narrow ? '8px 12px 10px' : '8px 20px 10px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          flex: 'none',
          paddingBottom: 10,
        }}
      >
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {rows.length} template{rows.length === 1 ? '' : 's'} in your library — reusable across
          campaigns
        </span>
        <div style={{ flex: 1 }} />
        <Button
          size="sm"
          variant="secondary"
          icon={<Ic.upload size={14} />}
          disabled={busy}
          onClick={() => setImporting(true)}
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-chrome)',
            color: 'var(--text-secondary)',
          }}
        >
          Import template
        </Button>
        <Button size="sm" icon={<Ic.plus size={14} />} disabled={busy} onClick={addTemplate}>
          New template
        </Button>
      </div>

      <QueryState
        loading={templates.loading}
        error={templates.error}
        empty={rows.length === 0}
        emptyTitle="No templates yet"
        emptyDescription="Write one or paste HTML you already have. Templates live in a library — you attach one to a campaign later, and the same template can back several."
        emptyAction={
          <Button icon={<Ic.plus size={14} />} disabled={busy} onClick={addTemplate}>
            New template
          </Button>
        }
        onRetry={templates.reload}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: narrow ? 'column' : 'row',
            gap: 14,
            alignItems: 'stretch',
          }}
        >
          <TemplateList
            templates={rows}
            activeId={activeId}
            onSelect={select}
            narrow={narrow}
          />

          <TemplateViewer
            rendered={rendered}
            sampleEmail={sampleLead.email}
            fromName={sender.fromName}
            replyTo={sender.replyTo}
            actions={
              <>
                <UsedByPill campaigns={usingCampaigns} onGo={() => onNavigate('campaigns')} />
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Ic.trash size={14} />}
                  onClick={() => draft && setConfirmDelete(draft)}
                >
                  Delete
                </Button>
                <Button size="sm" icon={<Ic.pencil size={14} />} onClick={() => setEditing(true)}>
                  Edit template
                </Button>
              </>
            }
          />
        </div>
      </QueryState>

      {dialogs}
    </div>
  )
}

// -----------------------------------------------------------------------------

/** "Used by 2 campaigns" — the reuse this library model exists for. */
function UsedByPill({ campaigns, onGo }: { campaigns: Campaign[]; onGo: () => void }) {
  if (!campaigns.length) {
    return (
      <span style={{ fontSize: 11.5, color: 'var(--text-faint)' }} title="Attach it from a campaign or the wizard">
        not attached to a campaign
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={onGo}
      title={campaigns.map((c) => c.name).join(', ')}
      style={{
        border: 'none',
        cursor: 'pointer',
        borderRadius: 999,
        padding: '2px 9px',
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 600,
        background: 'var(--accent-soft)',
        color: 'var(--accent-text)',
      }}
    >
      used by {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'}
    </button>
  )
}

/**
 * Deleting a library template is not a local decision — it can be the thing
 * several campaigns send. The count is read fresh, not guessed from the list.
 */
function DeleteTemplateDialog({
  template,
  busy,
  onClose,
  onDelete,
}: {
  template: Template
  busy: boolean
  onClose: () => void
  onDelete: () => void
}) {
  const users = useAsync(() => campaignsUsingTemplate(template.id), [template.id])
  const rows = users.data ?? []

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Delete "${template.name?.trim() || template.subject}"?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" disabled={busy || users.loading} onClick={onDelete}>
            {rows.length ? `Delete anyway` : 'Delete'}
          </Button>
        </>
      }
    >
      {users.loading ? (
        'Checking which campaigns use it…'
      ) : rows.length ? (
        <>
          <strong>
            Used by {rows.length} campaign{rows.length === 1 ? '' : 's'}
          </strong>{' '}
          ({rows.map((c) => c.name).join(', ')}). Deleting it leaves{' '}
          {rows.length === 1 ? 'that campaign' : 'those campaigns'} with no template, and their
          queued emails will fail with "no template bound" until you attach another one.
        </>
      ) : (
        'No campaign is using this template, so nothing is sending with it. This cannot be undone.'
      )}
    </Dialog>
  )
}
